import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Car,
    MessageSquare,
    Star,
    Banknote,
    Repeat,
    CalendarClock,
    LogOut,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

const NAV = [
    { to: "/admin", label: "Табло", icon: LayoutDashboard, end: true },
    { to: "/admin/cars", label: "Автомобили", icon: Car },
    { to: "/admin/messages", label: "Съобщения", icon: MessageSquare },
    { to: "/admin/testimonials", label: "Отзиви", icon: Star },
    { to: "/admin/finance", label: "Финансиране", icon: Banknote },
    { to: "/admin/tradein", label: "Бартер", icon: Repeat },
    { to: "/admin/calendar", label: "Google Calendar", icon: CalendarClock },
];

export default function AdminLayout() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();

    if (loading)
        return <div className="min-h-screen bg-bg text-ink-muted flex items-center justify-center">Зареждане…</div>;
    if (!user) return <Navigate to="/admin/login" replace />;

    const onLogout = () => {
        logout();
        navigate("/admin/login");
    };

    return (
        <div className="min-h-screen bg-bg flex flex-col lg:flex-row" data-testid="admin-layout">
            <aside className="lg:w-72 lg:min-h-screen bg-surface border-b lg:border-b-0 lg:border-r border-line p-6 lg:p-8">
                <Link to="/" className="flex items-center gap-3 mb-2">
                    <img
                        src="/ks-logo.png"
                        alt="Konstantin KS"
                        className="w-10 h-12 object-contain"
                    />
                    <span className="font-display text-2xl tracking-tight">
                        <span className="text-gradient-gold">Konstantin</span>
                        <span className="text-ink/80">.mobile.bg</span>
                    </span>
                </Link>
                <div className="text-[10px] uppercase tracking-[0.3em] text-ink-subtle mb-10">
                    Админ панел
                </div>
                <nav className="space-y-1">
                    {NAV.map(({ to, label, icon: Icon, end }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={end}
                            data-testid={`admin-nav-${label}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all ${
                                    isActive
                                        ? "bg-gold/10 text-gold border border-gold/30"
                                        : "text-ink/80 hover:bg-surface2 border border-transparent"
                                }`
                            }
                        >
                            <Icon size={16} /> {label}
                        </NavLink>
                    ))}
                </nav>

                <div className="mt-12 pt-6 border-t border-line">
                    <div className="text-xs text-ink-muted mb-2">{user.email}</div>
                    <button
                        onClick={onLogout}
                        data-testid="admin-logout"
                        className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-gold"
                    >
                        <LogOut size={14} /> Изход
                    </button>
                </div>
            </aside>

            <main className="flex-1 p-6 lg:p-10">
                <Outlet />
            </main>
        </div>
    );
}

export function AdminDashboard() {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        api.get("/admin/stats").then((r) => setStats(r.data));
    }, []);

    const cards = stats
        ? [
              { label: "Налични автомобили", value: stats.cars_available, color: "text-gold" },
              { label: "Продадени", value: stats.cars_sold, color: "text-ink" },
              { label: "Нови съобщения", value: stats.messages_unread, color: "text-gold" },
              { label: "Финансиране — нови", value: stats.finance_new, color: "text-gold" },
              { label: "Бартер — нови", value: stats.tradein_new, color: "text-gold" },
              { label: "Отзиви", value: stats.testimonials_total, color: "text-ink" },
          ]
        : [];

    return (
        <div data-testid="admin-dashboard">
            <h1 className="font-display text-4xl tracking-tightest mb-2">Табло</h1>
            <p className="text-ink-muted mb-10">Обобщение на дейността</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {cards.map((c) => (
                    <div
                        key={c.label}
                        className="bg-surface border border-line rounded-2xl p-7"
                    >
                        <div className="text-[10px] uppercase tracking-[0.3em] text-ink-subtle">
                            {c.label}
                        </div>
                        <div className={`font-display text-5xl mt-3 ${c.color}`}>
                            {c.value}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
